/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// other libraries
import { isEmpty } from 'lodash';
// hooks
import { useAuth } from '../../../hooks';
// redux
import { setReminderData } from '../../../redux/slices/toDoNotifications';
import { useSelector, dispatch } from '../../../redux/store';
import {
    userNotification,
    markAllNotification,
    markSingleReadNotification,
    markAllUnNotification,
    markSingleUnreadNotification,
    userTableNotification,
    snoozeNotification
} from '../../../redux/slices/user';
// Glass notification panel
import NotificationPanel from '../../../sections/notifications/NotificationPanel';
import { normalizeNotifications } from '../../../sections/notifications/normalizeNotification';
// call notifications (CU-869fmcc9r)
import { useCallFlow } from '../../../components/callFlow/CallFlowProvider';
import { patientPath } from '../../../components/callPill/patientPath';
import { CALL_ACTION, callFlowRow } from '../../../sections/notifications/callNotification';
// utils
import { getParsedData } from '../../../utils/storage-available';

// ----------------------------------------------------------------------

// Pagination tuning for the bell popover.
//   INITIAL_PAGE_SIZE: how many rows we fetch on first open. Previously 10,
//     which silently capped the "Notifications" pill at 10 even when the
//     user had 50+ unread. Raised to 25 so the first viewport is honest
//     without burning quota.
//   LOAD_MORE_PAGE_SIZE: subsequent infinite-scroll pages. Kept small so
//     each scroll-trigger feels snappy.
// True totals are sourced from redux state.user.unread (unread count) and
// state.user.notification.count (grand total) — those come from
// dedicated 1-row count probes and are NOT capped by these page sizes.
const INITIAL_PAGE_SIZE = 25;
const LOAD_MORE_PAGE_SIZE = 25;

export default function NotificationsPopover() {

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { notification: { results, count }, unread, tableType, isNotificationLoading } = useSelector((state) => state.user);
    const userDetail = useSelector((state) => state.practiceUser);

    const navigate = useNavigate();
    const callFlow = useCallFlow();

    const { user } = useAuth();

    const [isAdmin, setIsAdmin] = useState('');
    // Prevent multiple in-flight load-more requests during fast scrolling.
    // Cleared once the redux loading flag flips back to false.
    const loadMoreInFlight = useRef(false);

    // ── Init: load persisted reminder data ──────────────────────
    useEffect(() => {
        const savedNotifications = getParsedData('reminderData');
        if (!isEmpty(savedNotifications)) {
            dispatch(setReminderData(savedNotifications));
        }
        return () => {
            const snoozedData = getParsedData('timeout_ids') || [];
            if (!isEmpty(snoozedData)) {
                snoozedData?.forEach((element) => {
                    clearTimeout(element);
                });
            }
        };
    }, []);

    // Reset the in-flight guard once the underlying list grows OR the global
    // loading flag goes false. Listening to `results?.length` is the reliable
    // signal — the `userNotification` thunk doesn't always flip
    // `isNotificationLoading` (the slice never wires it for the all-tab
    // fetch), so relying on that alone left the guard stuck `true` after
    // the first page and silently broke infinite scroll. The extra
    // dependency on `isNotificationLoading` covers the case where the fetch
    // errors with no new rows.
    useEffect(() => {
        if (!isNotificationLoading) {
            loadMoreInFlight.current = false;
        }
    }, [isNotificationLoading, results?.length]);

    // ── Resolve current admin user ─────────────────────────────
    useEffect(() => {
        if (userDetail && userDetail?.practiceUser?.results?.length) {
            setIsAdmin(userDetail.practiceUser.results.find((users) => users.user.email === user.email));
        }
    }, [userDetail]);

    // Live notification triggers — when a new WP or email notification
    // arrives via PieSocket, these arrays grow. We use their lengths as
    // a signal to re-fetch the notification list from the API so the
    // bell panel stays up-to-date.
    const { wpNotifications, emailNotifications, refreshTick } = useSelector((state) => state.liveNotifications);
    const liveNotifCount = (wpNotifications?.length || 0) + (emailNotifications?.length || 0) + (refreshTick || 0);

    // ── Initial fetch: unread count + full list ────────────────
    useEffect(() => {
        if (isAdmin) {
            dispatch(userNotification(practiceId, isAdmin.id, INITIAL_PAGE_SIZE, 0, 'all', 'allTab'));
            dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'read', 'countLength'));
            dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'unread', 'countLength'));
        }
    }, [isAdmin]);

    // ── Re-fetch when live PieSocket notifications arrive ─────
    useEffect(() => {
        if (isAdmin && liveNotifCount > 0) {
            dispatch(userNotification(practiceId, isAdmin.id, INITIAL_PAGE_SIZE, 0, 'all', 'allTab'));
            dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'unread', 'countLength'));
        }
    }, [liveNotifCount]);

    // ── Normalize API data → glass component shape ─────────────
    const normalizedNotifications = useMemo(
        () => normalizeNotifications(results || []),
        [results]
    );

    // ── Refresh helper after actions ───────────────────────────
    // After a mark-read / snooze etc., re-pull as much of the list as the
    // user currently has loaded. Previously this only topped up by 10 if
    // `count > results.length` — for a 25-row open panel that left a gap
    // between row 25 and the next 10 rows fetched.
    const refreshAfterAction = useCallback(() => {
        if (!isAdmin) return;
        const loaded = results?.length || 0;
        if (count > loaded) {
            dispatch(userNotification(practiceId, isAdmin.id, LOAD_MORE_PAGE_SIZE, loaded, 'all'));
        }
        dispatch(userTableNotification(practiceId, isAdmin.id, 10, 0, 'table', (tableType === 'new')));
        dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'read', 'countLength'));
        dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'all', 'countLength'));
        dispatch(userNotification(practiceId, isAdmin.id, 1, 0, 'unread', 'countLength'));
    }, [isAdmin, practiceId, count, results?.length, tableType]);

    // ── Infinite scroll: panel calls this when it nears the bottom ──
    // Uses an in-flight ref to swallow duplicate triggers during fast scrolls.
    // Bails out cleanly if we've already loaded everything.
    const handleLoadMore = useCallback(() => {
        if (!isAdmin) return;
        const loaded = results?.length || 0;
        if (loaded >= (count || 0)) return; // nothing more to fetch
        if (loadMoreInFlight.current) return;
        loadMoreInFlight.current = true;
        dispatch(userNotification(practiceId, isAdmin.id, LOAD_MORE_PAGE_SIZE, loaded, 'all'));
    }, [isAdmin, practiceId, results?.length, count]);

    // ── Callbacks for NotificationPanel ─────────────────────────

    const handleMarkRead = useCallback((id) => {
        if (!isAdmin) return;
        const notification = normalizedNotifications.find((n) => n.id === id);
        if (notification?.isRead) {
            dispatch(markSingleUnreadNotification(practiceId, isAdmin.id, id, refreshAfterAction, 'popup'));
        } else {
            dispatch(markSingleReadNotification(practiceId, isAdmin.id, id, refreshAfterAction, 'popup'));
        }
    }, [isAdmin, practiceId, normalizedNotifications, refreshAfterAction]);

    const handleMarkAllRead = useCallback(() => {
        if (!isAdmin) return;
        if (unread > 0) {
            dispatch(markAllNotification(practiceId, isAdmin.id, refreshAfterAction));
        } else {
            dispatch(markAllUnNotification(practiceId, isAdmin.id, refreshAfterAction));
        }
    }, [isAdmin, practiceId, unread, refreshAfterAction]);

    const handleViewAll = useCallback(() => {
        navigate('/dashboard/user/notification-table');
    }, [navigate]);

    const handleOpenSettings = useCallback(() => {
        navigate('/dashboard/user/notification-settings');
    }, [navigate]);

    // Inbound-call rows: Open lead / Create lead / Call back. Using one marks the row read.
    const handleCallAction = useCallback((notification, actionKey) => {
        const view = notification.callView;
        if (!view) return false;
        const known = view.actions.some((a) => a.key === actionKey);
        if (!known) return true; // a call row never falls through to action_link navigation
        if (actionKey === CALL_ACTION.OPEN_LEAD) {
            if (practiceId && view.leadId) navigate(patientPath(practiceId, view.leadId));
        } else {
            const row = callFlowRow(view);
            if (!row) return true;
            if (actionKey === CALL_ACTION.CREATE_LEAD) callFlow.openCreateLead(row);
            else if (actionKey === CALL_ACTION.CALL_BACK) callFlow.openCallBack(row);
        }
        if (!notification.isRead && isAdmin) {
            dispatch(markSingleReadNotification(practiceId, isAdmin.id, notification.id, refreshAfterAction, 'popup'));
        }
        return true;
    }, [practiceId, navigate, callFlow, isAdmin, refreshAfterAction]);

    const handleAction = useCallback((id, actionKey) => {
        const notification = normalizedNotifications.find((n) => n.id === id);
        // A call row with its own buttons runs them; one without (withheld number, an older
        // missed-call row) opens its link like any other row (Call Logs), never a dead click.
        if (notification?.callView?.actions?.length) {
            handleCallAction(notification, actionKey);
            return;
        }
        if (notification?.action_link) {
            // Run through normalizeActionLink so legacy paths
            // ("practice/{pk}/analytics", "practice/{pk}/google-reviews", etc.)
            // resolve to current FE routes. Raw paths used to be navigated
            // verbatim — that worked for most verbs but bounced any
            // analytics-event notification (whatsapp_button_clicked,
            // phone_number_clicked) onto a non-existent route.
            const link = notification.action_link.startsWith('/')
                ? notification.action_link
                : `/dashboard/${normalizeActionLink(notification.action_link)}`;
            navigate(link, { state: { shouldRedirectToOverview: true } });
            if (!notification.isRead) {
                dispatch(markSingleReadNotification(practiceId, isAdmin.id, id, refreshAfterAction, 'popup'));
            }
        }
    }, [normalizedNotifications, navigate, isAdmin, practiceId, refreshAfterAction, handleCallAction]);

    const handleDismiss = useCallback((id) => {
        // Mark as read = dismiss
        if (!isAdmin) return;
        dispatch(markSingleReadNotification(practiceId, isAdmin.id, id, refreshAfterAction, 'popup'));
    }, [isAdmin, practiceId, refreshAfterAction]);

    const handleSnooze = useCallback((id, minutes) => {
        if (!isAdmin) return;
        dispatch(snoozeNotification(practiceId, isAdmin.id, id, minutes, refreshAfterAction));
    }, [isAdmin, practiceId, refreshAfterAction]);

    // ── Render glass panel ──────────────────────────────────────

    return (
        <NotificationPanel
            notifications={normalizedNotifications}
            // True totals from the dedicated count probes — NOT capped by
            // the page size of the list fetch. The panel uses these for
            // the bell badge and the "Notifications" pill so the user
            // never sees a misleading "10" when they actually have more.
            trueUnreadCount={unread || 0}
            totalCount={count || 0}
            // Infinite scroll plumbing.
            hasMore={(results?.length || 0) < (count || 0)}
            isLoadingMore={isNotificationLoading}
            onLoadMore={handleLoadMore}
            onAction={handleAction}
            onDismiss={handleDismiss}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
            onViewAll={handleViewAll}
            onOpenSettings={handleOpenSettings}
            onSnooze={handleSnooze}
        />
    );
}

// ── normalizeActionLink (used by NotificationListRow, LiveNotificationHandler) ──

export function normalizeActionLink(actionLink) {
    if (!actionLink) return '';
    // Reminder: "practice/{pk}/dashboard/tasks?taskId={pk}" → "tasks?taskId={pk}"
    const reminderMatch = actionLink.match(/^practice\/\d+\/dashboard\/(.+)$/);
    if (reminderMatch) return reminderMatch[1];
    // Facebook: "practice/{pk}/inbox/facebook" → "user/facebook"
    if (/^practice\/\d+\/inbox\/facebook$/.test(actionLink)) return 'user/facebook';
    // Analytics telemetry (phone_number_clicked, whatsapp_button_clicked):
    // BE now returns the click-tracking path directly, but any notifications
    // already persisted with the legacy "practice/{pk}/analytics" form must
    // also resolve to the click-tracking report — otherwise clicking
    // "View Analytics" lands on /404 and the state rehydration on that
    // path trips a "notifications is not iterable" crash.
    if (/^practice\/\d+\/analytics$/.test(actionLink)) return 'analytics-reports/click-tracking';
    if (actionLink === 'analytics-reports/click-tracking') return 'analytics-reports/click-tracking';
    // Google Reviews: "practice/{pk}/google-reviews" → "manage-business"
    if (/^practice\/\d+\/google-reviews$/.test(actionLink)) return 'manage-business';
    // Default: return as-is
    return actionLink;
}
